import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, mutualisteAPI, api } from '../services/api';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Vérifier le token au chargement
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      // Vérifier la validité du token
      authAPI.getProfile()
        .then(response => {
          setUser(response.data.user);
        })
        .catch(() => {
          // Token invalide, nettoyer le localStorage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { tokens } = response.data;
      
      // Stocker les tokens
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      
      // Configurer le header Authorization pour les prochaines requêtes
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
      
      // Récupérer le profil complet avec les données mutualiste
      // Passer le token explicitement dans les headers
      const profileResponse = await api.get('/auth/me', {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken}`
        }
      });
      const userData = profileResponse.data.user;
      
      setUser(userData);
      
      // Invalider le cache pour forcer le rechargement des données du nouvel utilisateur
      queryClient.clear();
      
      // Message de bienvenue adapté selon le rôle
      let welcomeMessage = 'Bienvenue !';
      if (userData.role === 'ADMIN') {
        welcomeMessage = 'Bienvenue Administrateur !';
      } else if (userData.mutualiste) {
        welcomeMessage = `Bienvenue ${userData.mutualiste.prenoms} ${userData.mutualiste.nom} !`;
      } else {
        welcomeMessage = `Bienvenue ${userData.email} !`;
      }
      
      toast.success(welcomeMessage);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Erreur de connexion';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Nettoyer les données locales
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      
      // Invalider tout le cache React Query pour éviter les données persistantes
      queryClient.clear();
      
      toast.success('Déconnexion réussie');
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const response = await authAPI.refreshToken(refreshToken);
      const { tokens } = response.data;
      
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      
      return tokens.accessToken;
    } catch (error) {
      // Refresh token invalide, déconnecter l'utilisateur
      logout();
      throw error;
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshToken,
    updateUser,
    isAdmin: user?.role === 'ADMIN',
    isMutualiste: user?.role === 'MUTUALISTE'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
